import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { supportedLanguages, type SupportedLanguage } from "@/i18n";
import ScrollToTopOnNavigate from "@/components/ui/ScrollToTopOnNavigate";
import ScrollToHash from "@/components/ui/ScrollToHash";
import SectionRedirect from "@/components/ui/SectionRedirect";
import HreflangTags from "@/components/ui/HreflangTags";
import Index from "./pages/Index";

// Lazy-loaded pages
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPage = lazy(() => import("./pages/Privacy"));
const TermsPage = lazy(() => import("./pages/Terms"));
const CookiesPage = lazy(() => import("./pages/Cookies"));
const AccessibilityPage = lazy(() => import("./pages/Accessibility"));
const SafeSustainableCommunities = lazy(() => import("./pages/programs/SafeSustainableCommunities"));
const GreenTalentDigitalFuture = lazy(() => import("./pages/programs/GreenTalentDigitalFuture"));
const PurposeDrivenSMEs = lazy(() => import("./pages/programs/PurposeDrivenSMEs"));
const AIForGoodResponsibleAI = lazy(() => import("./pages/programs/AIForGoodResponsibleAI"));
const CircularImpactGreenInShop = lazy(() => import("./pages/programs/CircularImpactGreenInShop"));
const PublicPage = lazy(() => import("./pages/Public"));
const LibraryPage = lazy(() => import("./pages/Library"));
const ResultsPage = lazy(() => import("./pages/transparency/Results"));
const SDGsPage = lazy(() => import("./pages/SDGs"));

// Admin Pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminRegister = lazy(() => import("./pages/admin/Register"));
const AdminForgotPassword = lazy(() => import("./pages/admin/ForgotPassword"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminLibrary = lazy(() => import("./pages/admin/Library"));
const AdminActivities = lazy(() => import("./pages/admin/Activities"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminIvyAnalytics = lazy(() => import("./pages/admin/IvyAnalytics"));
import ProtectedRoute from "./components/admin/ProtectedRoute";

const queryClient = new QueryClient();

/**
 * Component that syncs the URL locale parameter with i18next
 */
const LocaleSync = ({ children }: { children: React.ReactNode }) => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);

  return <>{children}</>;
};

/**
 * Redirects root / to /:detectedLocale/ based on i18next detection
 */
const RootRedirect = () => {
  const { i18n } = useTranslation();
  const lang = supportedLanguages.includes(i18n.language as SupportedLanguage)
    ? i18n.language
    : 'en';
  return <Navigate to={`/${lang}`} replace />;
};

/**
 * Redirects legacy non-prefixed paths to their locale-prefixed versions.
 * E.g., /library → /en/library
 */
const LegacyRedirect = ({ path }: { path: string }) => {
  const { i18n } = useTranslation();
  const lang = supportedLanguages.includes(i18n.language as SupportedLanguage)
    ? i18n.language
    : 'en';
  return <Navigate to={`/${lang}${path}`} replace />;
};

const LocalizedRoutes = () => (
  <LocaleSync>
    <HreflangTags />
    <Routes>
      <Route index element={<Index />} />
      <Route path="privacy" element={<PrivacyPage />} />
      <Route path="terms" element={<TermsPage />} />
      <Route path="cookies" element={<CookiesPage />} />
      <Route path="accessibility" element={<AccessibilityPage />} />
      <Route path="programs/safe-sustainable-communities" element={<SafeSustainableCommunities />} />
      <Route path="programs/green-talent-digital-future" element={<GreenTalentDigitalFuture />} />
      <Route path="programs/purpose-driven-smes" element={<PurposeDrivenSMEs />} />
      <Route path="programs/ai-for-good-responsible-ai" element={<AIForGoodResponsibleAI />} />
      <Route path="programs/circular-impact-greenin-shop" element={<CircularImpactGreenInShop />} />
      <Route path="public" element={<PublicPage />} />
      <Route path="library" element={<LibraryPage />} />
      <Route path="transparency/results" element={<ResultsPage />} />
      <Route path="sdgs" element={<SDGsPage />} />

      {/* Section alias redirects */}
      <Route path="programs" element={<SectionRedirect sectionId="programs" />} />
      <Route path="about" element={<SectionRedirect sectionId="about" />} />
      <Route path="contact" element={<SectionRedirect sectionId="contact" />} />
      <Route path="get-involved" element={<SectionRedirect sectionId="get-involved" />} />
      <Route path="ai-for-good" element={<SectionRedirect sectionId="ai-good" />} />
      <Route path="circular" element={<SectionRedirect sectionId="circular" />} />
      <Route path="circular-impact" element={<SectionRedirect sectionId="circular" />} />
      <Route path="transparency" element={<SectionRedirect sectionId="transparency" />} />
      <Route path="faq" element={<SectionRedirect sectionId="faq" />} />
      <Route path="ecosystem" element={<SectionRedirect sectionId="ecosystem" />} />
      <Route path="reinvestment" element={<SectionRedirect sectionId="reinvestment" />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  </LocaleSync>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <ScrollToTopOnNavigate />
              <ScrollToHash />
              <Suspense fallback={null}>
                <Routes>
                  {/* Root redirect to detected locale */}
                  <Route path="/" element={<RootRedirect />} />

                  {/* Locale-prefixed routes */}
                  <Route path="/:lang/*" element={<LocaleGuard />} />

                  {/* Admin routes (not localized) */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/register" element={<AdminRegister />} />
                  <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                  <Route path="/admin" element={<ProtectedRoute requiredRole="manager"><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole="manager"><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/leads" element={<ProtectedRoute requiredRole="manager"><AdminLeads /></ProtectedRoute>} />
                  <Route path="/admin/library" element={<ProtectedRoute requiredRole="admin"><AdminLibrary /></ProtectedRoute>} />
                  <Route path="/admin/activities" element={<ProtectedRoute requiredRole="admin"><AdminActivities /></ProtectedRoute>} />
                  <Route path="/admin/users" element={<ProtectedRoute requiredRole="super_admin"><AdminUsers /></ProtectedRoute>} />
                  <Route path="/admin/settings" element={<ProtectedRoute requiredRole="admin"><AdminSettings /></ProtectedRoute>} />
                  <Route path="/admin/inara-analytics" element={<ProtectedRoute requiredRole="admin"><AdminIvyAnalytics /></ProtectedRoute>} />

                  {/* Legacy non-prefixed routes — redirect to locale version */}
                  <Route path="/privacy" element={<LegacyRedirect path="/privacy" />} />
                  <Route path="/terms" element={<LegacyRedirect path="/terms" />} />
                  <Route path="/cookies" element={<LegacyRedirect path="/cookies" />} />
                  <Route path="/accessibility" element={<LegacyRedirect path="/accessibility" />} />
                  <Route path="/programs/safe-sustainable-communities" element={<LegacyRedirect path="/programs/safe-sustainable-communities" />} />
                  <Route path="/programs/green-talent-digital-future" element={<LegacyRedirect path="/programs/green-talent-digital-future" />} />
                  <Route path="/programs/purpose-driven-smes" element={<LegacyRedirect path="/programs/purpose-driven-smes" />} />
                  <Route path="/programs/ai-for-good-responsible-ai" element={<LegacyRedirect path="/programs/ai-for-good-responsible-ai" />} />
                  <Route path="/programs/circular-impact-greenin-shop" element={<LegacyRedirect path="/programs/circular-impact-greenin-shop" />} />
                  <Route path="/public" element={<LegacyRedirect path="/public" />} />
                  <Route path="/library" element={<LegacyRedirect path="/library" />} />
                  <Route path="/transparency/results" element={<LegacyRedirect path="/transparency/results" />} />
                  <Route path="/sdgs" element={<LegacyRedirect path="/sdgs" />} />
                  <Route path="/programs" element={<LegacyRedirect path="/programs" />} />
                  <Route path="/about" element={<LegacyRedirect path="/about" />} />
                  <Route path="/contact" element={<LegacyRedirect path="/contact" />} />
                  <Route path="/get-involved" element={<LegacyRedirect path="/get-involved" />} />
                  <Route path="/ai-for-good" element={<LegacyRedirect path="/ai-for-good" />} />
                  <Route path="/circular" element={<LegacyRedirect path="/circular" />} />
                  <Route path="/circular-impact" element={<LegacyRedirect path="/circular-impact" />} />
                  <Route path="/transparency" element={<LegacyRedirect path="/transparency" />} />
                  <Route path="/faq" element={<LegacyRedirect path="/faq" />} />
                  <Route path="/ecosystem" element={<LegacyRedirect path="/ecosystem" />} />
                  <Route path="/reinvestment" element={<LegacyRedirect path="/reinvestment" />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </TooltipProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </ThemeProvider>
  </QueryClientProvider>
);

/**
 * Guard that checks if :lang is a valid locale. 
 * If not, treat it as a 404 or legacy route.
 */
const LocaleGuard = () => {
  const { lang } = useParams<{ lang: string }>();
  
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <NotFound />;
  }

  return <LocalizedRoutes />;
};

export default App;
